import type {
  DownEventData,
  DownEventTopic,
  DownResponseData,
  DownResponseTopic,
} from '@/common/interop/down-messages';
import type { ConnectifiAppMetadata } from '@/common/types';
import type {
  AppMetadata,
  Context,
  IntentMetadata,
  DesktopAgent,
  AppIntent,
} from '@finos/fdc3';

export interface DirectoryIntent extends IntentMetadata {
  contexts?: Array<string>;
}

export enum ResolutionType {
  Intent = 'intent-resolver',
  Context = 'context-resolver',
}

export type DownEventHandler<TTopic extends DownEventTopic> = (
  data: DownEventData[TTopic],
) => void;
export type DownEventHandlers = {
  [K in keyof DownEventData]: DownEventHandler<K>;
};
export type DownResponseHandler<TTopic extends DownResponseTopic> = (
  data: DownResponseData[TTopic],
) => void;
export type DownResponseHandlerMap = {
  [K in keyof DownResponseData]: DownResponseHandler<K>;
};

/**
 * represents an FDC3 intent with a collection of related App Instances
 **/

export type AppIntentResult = {
  intent: IntentMetadata;
  apps: ConnectifiAppMetadata[];
};

export interface IntentResolutionMessage {
  resolutionType: ResolutionType;
  context: Context;
  data: AppIntentResult | AppIntentResult[];
  bridgeData?: AppIntent | AppIntent[];
  resultPendingId?: string;
}

export interface ResolveCallbackProps {
  selected: ConnectifiAppMetadata;
  intent: string;
  context: Context;
  bridge?: boolean;
  metadata?: any;
}

export type ResolveCallback = (props: ResolveCallbackProps) => void;

export type CloseCallback = () => void;

export type AuthenticateConfig = {
  /**
   * Prompts the user to authenticate.
   *
   * By default, the current window will be redirected to login page.
   * If redirect style is new window instead, the login page will be shown in a new browser window.
   *
   * Returns a promise that resolves when the user has successfully authenticated.
   */
  authenticate: (redirectStyle?: RedirectStyle) => Promise<void>;
} & DirectoryProps;

/**
 * interface for any Agent
 * @deprecated
 */
export type FDC3Agent = {
  connect: () => void;
} & Pick<AuthenticateConfig, 'authenticate'>;

export type RequestStorageAccessConfig = {
  /**
   * A button for users to request storage access.
   *
   * This button is embedded within an iframe on the interop domain, as modern browsers require user interaction
   * from the domain where third-party cookies reside. If a storage access request is initiated outside the interop
   * domain, the browser will deny it. Custom implementations should append this button to a DOM element within their UI.
   */
  button: HTMLIFrameElement;

  /**
   * Waits for the user to grant or deny storage access.
   *
   * This method should be called after the storage access button has been appended to the UI.
   *
   * Returns a promise that resolves to a boolean, indicating whether the user granted or denied storage access.
   */
  waitForStorageAccess: () => Promise<boolean>;

  /**
   * Requests user consent for third-party cookie usage.
   *
   * This is necessary only if a storage access request fails, suggesting that third-party cookies may be blocked.
   * By default, the current window is redirected to the consent page. If the redirect is set to new window,
   * the consent page will open in a new browser window.
   *
   * Returns a promise that resolves to a boolean, indicating whether the user granted or denied consent.
   */
  requestConsent: (redirectStyle?: RedirectStyle) => Promise<boolean>;
};

export interface AgentHandlers {
  /**
   * fdc3 api is ready for use
   * @param fdc3
   * @returns
   */
  onFDC3Ready?: (fdc3: DesktopAgent) => void;
  /**
   * directory session is established
   * @param directory directory properties
   * @param username if directory requires signin, the username is passed as well
   * @returns
   */
  onSessionStarted?: (directory: DirectoryProps, username?: string) => void;
  /**
   * session error - timeout connecting to service, bad app id, etc.
   * @param errorMessage
   * @returns
   */
  onSessionError?: (errorMessage: string) => void;

  /**
   * signin/login is complete
   * - applicable ONLY to directories with strict interop strategy
   * @param username
   * @returns
   */
  onSignedIn?: () => void;
  /**
   * signout/logout is complete
   * - applicable ONLY to directories with strict interop strategy
   * @returns
   */
  onSignedOut?: () => void;

  /**
   * channel joined - useful for setting any channel indicators
   * @param channelId id of the channel that was joined
   * @returns
   */
  onChannelJoined?: (channelId: string) => void;
  /**
   * channel left - useful for resetting any channel indicators
   * @returns
   */
  onChannelLeft?: () => void;
  /**
   * interop service is connected (useful for clearing any disconnected state/indicators)
   * @param initialConnect indicates if this is the initial connection to the service
   * @returns
   */
  onConnected?: (initialConnect: boolean) => void;
  /**
   * interop service is disconnected, it will attempt to reconnect in  "nextConnect" seconds.
   * useful for setting indicators/state like "disconnected, reconnecting in ..."
   * @param nextConnect next reconnect attempt in seconds
   * @returns
   */
  onDisconnected?: (nextConnect?: number) => void;
  /**
   * "working" or "busy" state of the agent
   * typically used for a "spinner" or "loading" indicator/effect
   * @param workInProgress
   * @returns
   */
  onWorkingChanged?: (workInProgress: boolean) => void;

  /**
   * Called when agent is requesting the user to authenticate.
   * @param directory the directory the user is authenticating against.
   */
  onSessionAuthRequired?: (directory: DirectoryProps) => void;

  /**
   * for implementing a custom intent resolver
   * @param message
   * @param callback
   * @param closeCallback
   * @returns
   */
  handleIntentResolution?: (
    message: IntentResolutionMessage,
    callback: ResolveCallback,
    closeCallback: CloseCallback,
  ) => void;

  /**
   * for overriding the default "open" implementation (window.open)
   * @param message
   * @returns
   */
  handleOpen?: (message: ConnectifiOpenMessage) => void;

  /**
   * for overriding the default FAB click operation
   * @param message
   * @returns
   */
  handleFABClicked?: (event: MouseEvent) => void;

  /**
   * Handles authenticating the user for directories that require authentication.
   * Applicable ONLY to directories with strict interop strategy.
   * @param directory directory properties
   * @returns promise that resolves when authentication of the user is successful.
   */
  handleAuthenticate?: (config: AuthenticateConfig) => Promise<void>;

  /**
   * Overrides the default behavior for requesting storage access.
   */
  handleRequestStorageAccess?: (
    config: RequestStorageAccessConfig,
  ) => Promise<void>;
}

export interface AgentConfig extends AgentHandlers {
  props?: FabProps;
  headless?: boolean;
  logLevel?: LogLevel;
  bridgeGlobal?: boolean;
  // for overriding the default logging implementation (console.log)
  logger?: (...params: any) => void;
}

export interface AgentConfigInfo {
  bridgeGlobal?: boolean;
  headless?: boolean;
  logLevel?: LogLevel;
  props?: FabProps;
  activeHandlers: AgentConfigHandlerInfo;
}

export type AgentConfigHandlerInfo = { [P in keyof AgentHandlers]?: true };

export type LogLevel = 'debug' | 'info' | 'silent';
export interface Logger {
  info: (...params: any) => void;
  debug: (...params: any) => void;
  error: (...params: any) => void;
}

export type ValidPositions = 'tl' | 'ml' | 'bl' | 'tr' | 'mr' | 'br';

export type RedirectStyle = 'sameWindow' | 'newWindow';

export interface FabProps {
  logoSrc?: string;
  position?: ValidPositions;
  loginStyle?: RedirectStyle;
}

export interface DirectoryProps {
  name: string;
  interopStrategy: 'open' | 'openauth' | 'app' | 'strict';
  icon?: string;
}

export type FabToastType =
  | 'success'
  | 'error'
  | 'login'
  | 'reconnect'
  | 'session'
  | 'timeout';

/**
 * interface for UI component binding to an Agent
 */
export interface AgentGUI {
  agent: FDC3Agent | undefined;
  connected: boolean;
  logoSrc?: string;
  bind: (agent: FDC3Agent) => void;

  // allow override of the default click action
  handleClick?: (event: MouseEvent) => void;

  onConnected: (initialConnect: boolean) => void;
  onDisconnected: (nextConnect?: number) => void;
  onFDC3Ready: (fdc3: DesktopAgent) => void;
  onSessionStarted: (directory: DirectoryProps, username?: string) => void;
  onSessionError: (errorMessage: string) => void;
  onSignedOut: () => void;
  onWorkingChanged: (working: boolean) => void;
  onChannelJoined: (channelId: string) => void;
  onChannelLeft: () => void;
  handleIntentResolution: (
    message: IntentResolutionMessage,
    callback: ResolveCallback,
    closeCallback: CloseCallback,
  ) => void;
}

export interface ConnectifiMessageData {
  target?: AppMetadata | string | undefined;
  context?: Context;
  intent?: string;
  id?: string;
  contextType?: string;
  channelId?: string;
  channel?: string;
  name?: string | AppMetadata;
  appId?: string;
  src?: string;
  title?: string;
  stateId?: string;
  url?: string;
  pendingId?: string;
  resultPendingId?: string;
}

/**
 * todo: change label "fdc3Token" to "token"
 */
export interface ConnectifiMessage {
  topic: string;
  appId: string;
  eventId: string;
  data:
    | ConnectifiMessageData
    | AppIntentResult
    | AppIntentResult[]
    | JoinChannelResult;
  resultPendingId?: string;
  context?: Context;
}

export interface JoinChannelResult {
  channel: string;
}

export interface TopicDataPair {
  topic: string;
  data?: ConnectifiMessageData;
}

export interface AgentState {
  connected?: boolean;
  owner?: string | null;
  channel?: string | undefined;
}

export enum ConnectionError {
  NoConnectionAvailable = 'NoConnectionAvailable',
}

export interface ConnectifiOpenMessage {
  name?: string;
  appId?: string;
  url?: string;
}
